import React, { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { TID } from "../lib/testIds";
import { extractDocument, createAnalysis } from "../lib/api";
import {
  Upload,
  FileText,
  ArrowRight,
  ArrowLeft,
  Loader2,
  CheckCircle2,
  Building2,
  ShieldCheck,
  Briefcase,
} from "lucide-react";
import { toast } from "sonner";

const profileSchema = [
  {
    label: "Company Identity",
    icon: Building2,
    fields: [
      { name: "company_name", label: "Company Name", placeholder: "Acme Federal Solutions LLC" },
      { name: "years_in_business", label: "Years in Business", placeholder: "12" },
      { name: "annual_revenue", label: "Annual Revenue", placeholder: "$24M" },
      { name: "number_of_employees", label: "Number of Employees", placeholder: "85" },
      { name: "geographic_coverage", label: "Geographic Coverage", placeholder: "CONUS / OCONUS / DMV" },
    ],
  },
  {
    label: "Classification & Set-Asides",
    icon: ShieldCheck,
    fields: [
      { name: "primary_naics", label: "Primary NAICS", placeholder: "541512" },
      { name: "secondary_naics", label: "Secondary NAICS", placeholder: "541330, 541611" },
      { name: "business_size", label: "Business Size", placeholder: "Small / Large" },
      { name: "small_business_status", label: "Small Business Status", placeholder: "Yes / No" },
      { name: "sdvosb_status", label: "SDVOSB", placeholder: "Yes / No" },
      { name: "wosb_status", label: "WOSB / EDWOSB", placeholder: "Yes / No" },
      { name: "hubzone_status", label: "HUBZone", placeholder: "Yes / No" },
      { name: "eight_a_status", label: "8(a) Status", placeholder: "Active / Graduated / No" },
    ],
  },
  {
    label: "Capabilities & Past Performance",
    icon: Briefcase,
    fields: [
      { name: "core_capabilities", label: "Core Capabilities", placeholder: "Cyber, cloud migration, C5ISR, software dev...", type: "textarea" },
      { name: "relevant_past_performance", label: "Relevant Past Performance", placeholder: "List prior contracts, agencies, dollar values, roles...", type: "textarea" },
      { name: "existing_contract_vehicles", label: "Existing Contract Vehicles", placeholder: "GSA MAS, SEWP V, OASIS+, CIO-SP4...", type: "textarea" },
      { name: "security_clearances", label: "Security Clearances", placeholder: "Facility: TS/SCI. Personnel: 12 TS, 30 Secret", type: "textarea" },
      { name: "key_certifications", label: "Key Certifications", placeholder: "CMMC L2, ISO 27001, CMMI Dev 3, FedRAMP...", type: "textarea" },
      { name: "teaming_partners", label: "Teaming Partners", placeholder: "Names, roles, complementary capabilities...", type: "textarea" },
    ],
  },
];

const emptyProfile = profileSchema
  .flatMap((g) => g.fields.map((f) => f.name))
  .reduce((a, k) => ({ ...a, [k]: "" }), {});

export default function NewAnalysis() {
  const nav = useNavigate();
  const fileInputRef = useRef(null);
  const [step, setStep] = useState(1); // 1: solicitation, 2: profile, 3: review
  const [mode, setMode] = useState("upload"); // upload | paste
  const [filename, setFilename] = useState("");
  const [text, setText] = useState("");
  const [extracting, setExtracting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [profile, setProfile] = useState({ ...emptyProfile, company_name: "" });
  const [access, setAccess] = useState(null); // { token, tier, session_id }

  React.useEffect(() => {
    const readToken = () => {
      try {
        const raw = localStorage.getItem("govbidiq_access");
        if (raw) {
          const parsed = JSON.parse(raw);
          setAccess((prev) => {
            // Only toast when transitioning to a paid state
            if (parsed?.token && !prev?.token) {
              toast.success(`Access token active — ${parsed.tier || "opportunity"} tier`);
            }
            return parsed;
          });
        } else {
          setAccess(null);
        }
      } catch (_) { /* ignore */ }
    };

    readToken();

    // Re-read when another tab writes the token, or when this tab regains focus
    const onStorage = (e) => {
      if (e.key === "govbidiq_access" || e.key === null) readToken();
    };
    const onFocus = () => readToken();
    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, []);

  const handleFile = useCallback(async (f) => {
    if (!f) return;
    setExtracting(true);
    setFilename(f.name);
    try {
      const data = await extractDocument(f);
      setText(data.text);
      toast.success(`Extracted ${data.characters.toLocaleString()} characters`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Extraction failed");
      setFilename("");
    } finally {
      setExtracting(false);
    }
  }, []);

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  const submit = async () => {
    if (text.trim().length < 200) {
      toast.error("Solicitation text is too short (min 200 characters).");
      return;
    }
    if (!access?.token) {
      toast.error("A paid access token is required. Please purchase a report first.");
      nav("/pricing");
      return;
    }
    setSubmitting(true);
    try {
      const res = await createAnalysis({
        solicitation_text: text,
        document_name: filename || "Pasted text",
        contractor_profile: profile,
        access_token: access.token,
      });
      // Consume token
      try { localStorage.removeItem("govbidiq_access"); } catch (_) { /* ignore */ }
      toast.success("Analysis started");
      nav(`/report/${res.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create analysis");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grain min-h-screen">
      <Navbar />

      <div className="max-w-[1200px] mx-auto px-6 py-12">
        {/* Access banner */}
        {access?.token ? (
          <div data-testid="access-banner-paid" className="mb-8 border-l-2 border-emerald-500 bg-emerald-500/5 p-4 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="tick-label text-emerald-400 mb-1">/ Paid access confirmed</div>
              <div className="text-sm text-slate-200 font-mono">
                Tier: <span className="text-emerald-300">{access.tier}</span> · Token: <span className="text-slate-400">{access.token.slice(0, 8)}…</span>
              </div>
            </div>
            <div className="text-xs text-slate-500 font-mono">Token will be consumed on submission.</div>
          </div>
        ) : (
          <div data-testid="access-banner-unpaid" className="mb-8 border-l-2 border-amber-500 bg-amber-500/5 p-4 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="tick-label text-amber-400 mb-1">/ No active token</div>
              <div className="text-sm text-slate-200">
                Purchase a report tier before running an analysis.
              </div>
            </div>
            <button
              onClick={() => nav("/pricing")}
              className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-400 text-slate-950 font-mono text-[10px] tracking-[0.2em] uppercase font-bold px-4 py-2.5"
            >
              View Pricing
            </button>
          </div>
        )}

        {/* Stepper */}
        <div className="flex items-center gap-3 mb-10">
          {[
            { n: 1, t: "Solicitation" },
            { n: 2, t: "Contractor Profile" },
            { n: 3, t: "Review & Run" },
          ].map((s, i) => (
            <React.Fragment key={s.n}>
              <div className="flex items-center gap-2.5">
                <div
                  className={`w-7 h-7 flex items-center justify-center font-mono text-xs border ${
                    step === s.n
                      ? "bg-sky-500 text-slate-950 border-sky-500"
                      : step > s.n
                      ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/40"
                      : "bg-transparent text-slate-500 border-slate-700"
                  }`}
                >
                  {step > s.n ? <CheckCircle2 className="w-4 h-4" /> : s.n}
                </div>
                <div
                  className={`tick-label ${
                    step === s.n ? "text-slate-100" : step > s.n ? "text-emerald-400" : "text-slate-500"
                  }`}
                >
                  {s.t}
                </div>
              </div>
              {i < 2 && <div className="flex-1 h-px bg-slate-800" />}
            </React.Fragment>
          ))}
        </div>

        {/* STEP 1: SOLICITATION */}
        {step === 1 && (
          <div className="panel p-6 sm:p-8">
            <h1 className="font-display font-black text-2xl sm:text-3xl text-slate-100 mb-2">
              Ingest the solicitation
            </h1>
            <p className="text-slate-400 text-sm mb-8">
              Upload the RFP / RFQ / SOW (PDF, DOCX, TXT) or paste the text directly.
            </p>

            <div className="flex gap-px bg-[var(--border-soft)] mb-6 w-fit">
              <button
                data-testid={TID.uploadTab}
                onClick={() => setMode("upload")}
                className={`px-5 py-2.5 font-mono text-[11px] tracking-[0.2em] uppercase transition-colors ${
                  mode === "upload"
                    ? "bg-sky-500 text-slate-950"
                    : "bg-[var(--bg-secondary)] text-slate-400 hover:text-slate-100"
                }`}
              >
                Upload File
              </button>
              <button
                data-testid={TID.pasteTab}
                onClick={() => setMode("paste")}
                className={`px-5 py-2.5 font-mono text-[11px] tracking-[0.2em] uppercase transition-colors ${
                  mode === "paste"
                    ? "bg-sky-500 text-slate-950"
                    : "bg-[var(--bg-secondary)] text-slate-400 hover:text-slate-100"
                }`}
              >
                Paste Text
              </button>
            </div>

            {mode === "upload" ? (
              <div
                data-testid={TID.uploadDropzone}
                onDragOver={(e) => e.preventDefault()}
                onDrop={onDrop}
                onClick={() => fileInputRef.current?.click()}
                className="border border-dashed border-slate-700 hover:border-sky-500 bg-[var(--bg-secondary)] p-12 text-center cursor-pointer transition-colors"
              >
                <input
                  ref={fileInputRef}
                  data-testid={TID.uploadFileInput}
                  type="file"
                  accept=".pdf,.docx,.txt"
                  className="hidden"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                />
                {extracting ? (
                  <>
                    <Loader2 className="w-8 h-8 text-sky-400 mx-auto mb-3 animate-spin" />
                    <div className="font-mono text-xs tracking-widest uppercase text-sky-400">
                      Extracting {filename}...
                    </div>
                  </>
                ) : filename ? (
                  <>
                    <FileText className="w-8 h-8 text-emerald-400 mx-auto mb-3" />
                    <div data-testid={TID.uploadFilename} className="font-mono text-sm text-slate-100">{filename}</div>
                    <div className="tick-label mt-1">{text.length.toLocaleString()} characters extracted · click to replace</div>
                  </>
                ) : (
                  <>
                    <Upload className="w-8 h-8 text-slate-500 mx-auto mb-3" strokeWidth={1.5} />
                    <div className="text-slate-200 mb-1">Drag &amp; drop or click to upload</div>
                    <div className="tick-label">PDF · DOCX · TXT · up to ~100 pages</div>
                  </>
                )}
              </div>
            ) : (
              <textarea
                data-testid={TID.pasteTextarea}
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={16}
                placeholder="Paste the full solicitation text here..."
                className="w-full bg-[var(--bg-secondary)] border border-slate-700 focus:border-sky-500 outline-none text-slate-100 font-mono text-[13px] leading-relaxed p-4 resize-y"
              />
            )}

            {text && mode === "upload" && (
              <div className="mt-6">
                <div className="tick-label mb-2">Preview · first 600 chars</div>
                <div
                  data-testid={TID.extractedPreview}
                  className="bg-[var(--bg-secondary)] border border-slate-800 p-4 font-mono text-[11px] text-slate-400 max-h-40 overflow-auto leading-relaxed"
                >
                  {text.slice(0, 600)}
                  {text.length > 600 ? "..." : ""}
                </div>
              </div>
            )}

            <div className="flex justify-end mt-8">
              <button
                data-testid={TID.nextStepBtn}
                disabled={text.trim().length < 200}
                onClick={() => setStep(2)}
                className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-400 disabled:bg-slate-800 disabled:text-slate-600 text-slate-950 font-mono text-[11px] tracking-[0.2em] uppercase font-bold px-5 py-3 transition-colors"
              >
                Continue <ArrowRight className="w-4 h-4" strokeWidth={3} />
              </button>
            </div>
          </div>
        )}

        {/* STEP 2: PROFILE */}
        {step === 2 && (
          <div className="space-y-px bg-[var(--border-soft)]">
            <div className="bg-[var(--bg-secondary)] p-6 sm:p-8">
              <h1 className="font-display font-black text-2xl sm:text-3xl text-slate-100 mb-2">
                Contractor profile
              </h1>
              <p className="text-slate-400 text-sm">
                Provide as much detail as possible. The match assessment quality scales directly with profile depth.
                Fields you leave blank are treated as &quot;Not provided.&quot;
              </p>
            </div>

            {profileSchema.map((group) => (
              <div key={group.label} className="bg-[var(--bg-secondary)] p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-5">
                  <group.icon className="w-5 h-5 text-sky-400" strokeWidth={1.6} />
                  <h2 className="font-display font-bold text-slate-100">{group.label}</h2>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  {group.fields.map((f) => (
                    <div
                      key={f.name}
                      className={f.type === "textarea" ? "sm:col-span-2" : ""}
                    >
                      <label className="tick-label block mb-1.5">{f.label}</label>
                      {f.type === "textarea" ? (
                        <textarea
                          data-testid={TID.field(f.name)}
                          value={profile[f.name] || ""}
                          onChange={(e) => setProfile({ ...profile, [f.name]: e.target.value })}
                          placeholder={f.placeholder}
                          rows={3}
                          className="w-full bg-[var(--bg-primary)] border border-slate-700 focus:border-sky-500 outline-none text-slate-100 text-sm p-3 font-mono leading-relaxed resize-y"
                        />
                      ) : (
                        <input
                          data-testid={TID.field(f.name)}
                          value={profile[f.name] || ""}
                          onChange={(e) => setProfile({ ...profile, [f.name]: e.target.value })}
                          placeholder={f.placeholder}
                          className="w-full bg-[var(--bg-primary)] border border-slate-700 focus:border-sky-500 outline-none text-slate-100 text-sm p-3 font-mono"
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div className="bg-[var(--bg-secondary)] p-6 sm:p-8 flex items-center justify-between">
              <button
                data-testid={TID.prevStepBtn}
                onClick={() => setStep(1)}
                className="inline-flex items-center gap-2 border border-slate-700 hover:border-slate-500 text-slate-300 font-mono text-[11px] tracking-[0.2em] uppercase px-5 py-3"
              >
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <button
                data-testid={TID.nextStepBtn}
                onClick={() => setStep(3)}
                className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-400 text-slate-950 font-mono text-[11px] tracking-[0.2em] uppercase font-bold px-5 py-3 transition-colors"
              >
                Continue <ArrowRight className="w-4 h-4" strokeWidth={3} />
              </button>
            </div>
          </div>
        )}

        {/* STEP 3: REVIEW */}
        {step === 3 && (
          <div className="panel p-6 sm:p-8">
            <h1 className="font-display font-black text-2xl sm:text-3xl text-slate-100 mb-2">
              Review &amp; run analysis
            </h1>
            <p className="text-slate-400 text-sm mb-8">
              Confirm the inputs. The Claude Sonnet 4.5 engine will produce a 20-section
              Opportunity Intelligence Report.
            </p>

            <div className="grid md:grid-cols-2 gap-px bg-[var(--border-soft)] mb-8">
              <div className="bg-[var(--bg-primary)] p-5">
                <div className="tick-label mb-2 text-sky-400">/ Solicitation</div>
                <div className="text-slate-100 mb-1">{filename || "Pasted text"}</div>
                <div className="font-mono text-xs text-slate-500">
                  {text.length.toLocaleString()} characters
                </div>
              </div>
              <div className="bg-[var(--bg-primary)] p-5">
                <div className="tick-label mb-2 text-sky-400">/ Contractor</div>
                <div className="text-slate-100 mb-1">{profile.company_name || "Unnamed contractor"}</div>
                <div className="font-mono text-xs text-slate-500">
                  NAICS {profile.primary_naics || "—"} · {profile.business_size || "Size N/A"}
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-4">
              <button
                onClick={() => setStep(2)}
                className="inline-flex items-center gap-2 border border-slate-700 hover:border-slate-500 text-slate-300 font-mono text-[11px] tracking-[0.2em] uppercase px-5 py-3"
              >
                <ArrowLeft className="w-4 h-4" /> Edit Profile
              </button>
              <button
                data-testid={TID.submitAnalysisBtn}
                disabled={submitting}
                onClick={submit}
                className="inline-flex items-center gap-3 bg-sky-500 hover:bg-sky-400 disabled:bg-slate-800 disabled:text-slate-600 text-slate-950 font-mono text-xs tracking-[0.2em] uppercase font-bold px-7 py-4 transition-colors"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Launching engine...
                  </>
                ) : (
                  <>
                    Generate Intelligence Report <ArrowRight className="w-4 h-4" strokeWidth={3} />
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
